import React, { useState } from 'react';
import AnimatedNumberCounter from './animated-number-counter';
import PropTypes from 'prop-types';
import Image from '$partials/media/image.jsx';
import { Button } from '$partials/ui';
import Carousel, { CarouselNavigation, CarouselSlide } from '$partials/ui/carousel';
import { CarouselProvider } from '$contexts/carousel';
import { useLocale } from '$contexts/locale';


/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} ModulePartnerSpotlightPropTypes
 */

const SpotlightCarouselItem = ({
    body,
    statistic,
    statisticInfo,
    logo,
    caseStudy
}) => {
    const { translate: t } = useLocale();
    const [progress, setProgress] = useState(-1);
    const rx = /(?<number>[\d|.]+)|(?<text>[\D]+)/g;
    let matches, output = [];

    while (matches = rx.exec(statistic)) {
        output.push(matches.groups);
    }

    const handleAnimationEnd = (i) => setProgress(i);
    let shouldRenderItem = true;

    return(
        <>
            <div className='p-50 w-full rounded-button bg-sand'>
                <div className='flex flex-col h-full justify-between'>
                    <div className='flex flex-col justify-end'>
                        {logo &&
                            <Image
                                className='object-contain object-left-top w-[140px] h-[40px]'
                                loading={''}
                                loadWidth={140}
                                src={logo.src}
                                style={{width: undefined}} />
                        }
                        <div className='flex flex-row mt-60 md:mb-8 mb-10 md:mt-80'>
                            {output.map((item, i) => {
                                if (!shouldRenderItem) { return null; }
                                const result = (
                                    item.number ? (
                                        <div
                                            className='relative type-style-6'
                                            key={i}>
                                            <div className='absolute'>
                                                <AnimatedNumberCounter
                                                    onEnd={handleAnimationEnd.bind(null, i)}
                                                    toValue={parseFloat(item.number)} />
                                            </div>
                                            <span className='opacity-0'>{item.number}</span>
                                        </div>
                                    ) : (
                                        <span
                                            className='type-style-6'
                                            key={i}>{item.text}</span>
                                    )
                                );

                                if (i > progress && item.number && shouldRenderItem) {
                                    shouldRenderItem = false;
                                }
                                return result;
                            })}
                        </div>
                        <p className='type-style-5'>{statisticInfo}</p>
                        <p className='type-style-5 text-light-grey'>{body}</p>
                    </div>
                    {caseStudy && (
                        <div className='w-full md:w-auto flex mt-20'>
                            <Button
                                as='a'
                                className='flex self-start items-center bg-orange text-white rounded-button type-style-7 px-20 py-13'
                                href={`/case-studies/${caseStudy.slug}`}
                                type='button'>
                                {t('See Case Study')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const ModuleSpotlightCarousel = ({
    itemsCollection,
    title,
    parent,
}) => {
    const items = itemsCollection.items;

    return (
        <div className={`bg-white relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>
                <div className='bg-white'>
                    <div className={`hidden ${items.length > 3 ? '' : 'md:flex'} px-10`}>
                        <p className='type-style-3'>{title}</p>
                    </div>
                    <div className={`hidden ${items.length > 3 ? '' : 'md:flex'} mt-40`}>
                        {items.map((item, i) => item ? (
                            <div
                                className={`flex flex-wrap w-11/12 sm:w-5/12 ${items.length <= 2 ? 'md:w-1/2' : 'md:w-1/3'} px-10 sm:px-5 md:px-10`}
                                key={i}>
                                <SpotlightCarouselItem
                                    className='w-full'
                                    {...item} />
                            </div>
                        ) : null )}
                    </div>
                    <div className={`overflow-hidden w-screen max-w-screen left-1/2 relative -translate-x-1/2 mt-40 pl-10 sm:pl-0 ${items.length > 3 ? '' : 'md:hidden'}`}>
                        <CarouselProvider>
                            <div className='layout-container'>
                                <div className={`flex flex-col gap-10 sm:flex-row sm:px-5 md:px-10 items-center ${title ? 'justify-between' : 'justify-end'}`}>
                                    <div className='w-full sm:w-7/12'>
                                        <p className='type-style-3'>{title}</p>
                                    </div>
                                    <CarouselNavigation className='self-start hidden sm:inline-block'/>
                                </div>
                            </div>
                            <div className='layout-container overflow-visible'>
                                <div className='-mx-10 sm:mx-0'>
                                    <Carousel className='mt-[32px] sm:mt-40 mb-36 flex swiper-wrapper h-auto overflow-visible'>
                                        {items.map((item, i) => {
                                            return (
                                                <CarouselSlide
                                                    className='h-auto flex flex-wrap w-11/12 sm:w-5/12 md:w-1/3 px-10 sm:px-5 md:px-10'
                                                    index={i}
                                                    key={i}>
                                                    <SpotlightCarouselItem
                                                        className='w-full'
                                                        key={i}
                                                        {...item} />
                                                </CarouselSlide>
                                            );}
                                        )}
                                    </Carousel>
                                </div>
                            </div>
                        </CarouselProvider>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ModuleSpotlightCarouselPropTypes = {
    itemsCollection: PropTypes.object,
    title: PropTypes.string,
    parent: PropTypes.string
};

const SpotlightCarouselItemPropTypes = {
    body: PropTypes.string,
    statisticInfo: PropTypes.string,
    statistic: PropTypes.string,
    logo: PropTypes.object,
    caseStudy: PropTypes.object
};

ModuleSpotlightCarousel.propTypes = ModuleSpotlightCarouselPropTypes;
SpotlightCarouselItem.propTypes = SpotlightCarouselItemPropTypes;

export default ModuleSpotlightCarousel;
